import { useCallback, useEffect, useState } from 'react';

import { useGetRoadmapItemsQuery } from '../../redux/api/roadmap';
import { allIds, countUnseen } from './grouping';
import type { RoadmapItem } from './types';

export const SEEN_IDS_KEY = 'fastlane:updates:seenIds';

/**
 * Slow poll so the badge stays honest on tablets that sit open for days.
 * The window itself refetches on open; this only keeps the count current.
 */
export const POLL_INTERVAL_MS = 15 * 60 * 1000;

/**
 * localStorage is unavailable in private windows and when a browser blocks
 * site data, so every read and write is guarded — a failure just means no
 * badge, never a broken sidebar.
 */
function readSeenIds(): number[] | null {
	try {
		const raw = window.localStorage.getItem(SEEN_IDS_KEY);
		if (raw === null) return null;
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return null;
		return parsed.filter((value) => Number.isFinite(value));
	} catch {
		return null;
	}
}

function writeSeenIds(ids: number[]): void {
	try {
		window.localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(ids));
	} catch {
		/* no badge state kept; nothing else depends on it */
	}
}

export function useUnseenUpdates() {
	const { data, isLoading, refetch } = useGetRoadmapItemsQuery(undefined, {
		pollingInterval: POLL_INTERVAL_MS,
	});
	const items = data ?? [];

	const [seenIds, setSeenIds] = useState<number[] | null>(readSeenIds);
	// True when the user opened the window before the query resolved — the
	// mark is honoured once data arrives, so the badge cannot go stale behind
	// an already-viewed modal.
	const [markPending, setMarkPending] = useState(false);

	const storeSeen = useCallback((toStore: RoadmapItem[]) => {
		const ids = allIds(toStore);
		writeSeenIds(ids);
		setSeenIds(ids);
	}, []);

	// A user who has never opened the window has not missed anything, so
	// their first load sets the baseline instead of badging the whole backlog.
	useEffect(() => {
		if (seenIds !== null || items.length === 0) return;
		storeSeen(items);
	}, [items, seenIds, storeSeen]);

	const markSeen = useCallback(() => {
		if (items.length === 0) {
			setMarkPending(true);
			return;
		}
		storeSeen(items);
		setMarkPending(false);
	}, [items, storeSeen]);

	useEffect(() => {
		if (!markPending || items.length === 0) return;
		storeSeen(items);
		setMarkPending(false);
	}, [markPending, items, storeSeen]);

	return {
		items,
		isLoading,
		unseenCount: countUnseen(items, seenIds),
		markSeen,
		/** Refetch on demand — the layouts call this when the window opens. */
		refresh: refetch,
	};
}
